import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Phone,
  ShieldCheck,
  Zap,
  Infinity as InfinityIcon,
  MapPin,
  Smartphone,
  Repeat,
  CheckCircle2,
  ArrowRight,
  Star,
  Lock,
  ChevronRight,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  LogOut,
  Trash2,
  RefreshCw,
} from "lucide-react";
import "@/App.css";
import { TermsAndConditionsPage, PrivacyPolicyPage, RefundPolicyPage } from "./LegalPages";
import { QLDPage, NSWPage, VICPage, WAPage, SAPage, TASPage, ACTPage, NTPage } from "./StatePages";
import { PrisonPageFor, PRISON_SLUGS } from "./PrisonPages";
import {
  PrisonConnectAlternativePage,
  CellblockBVsPrisonConnectPage,
  PrisonEngineAlternativePage,
  CellblockBVsPrisonEnginePage,
  CheapestPrisonCallPage,
} from "./CompetitorPages";
import AnalyticsDebugPage from "./AnalyticsDebugPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRISONS = [
  // ACT
  "ACT — Alexander Maconochie Centre",
  // NSW
  "NSW — Bathurst Correctional Centre",
  "NSW — Cessnock Correctional Centre",
  "NSW — Cooma Correctional Centre",
  "NSW — Dillwynia Correctional Centre",
  "NSW — Goulburn Correctional Centre",
  "NSW — Junee Correctional Centre",
  "NSW — Kirkconnell Correctional Centre",
  "NSW — Lithgow Correctional Centre",
  "NSW — Long Bay Correctional Complex",
  "NSW — Macquarie Correctional Centre",
  "NSW — Mid North Coast Correctional Centre",
  "NSW — Parklea Correctional Centre",
  "NSW — Silverwater Correctional Complex",
  "NSW — South Coast Correctional Centre",
  "NSW — Wellington Correctional Centre",
  // NT
  "NT — Alice Springs Correctional Centre",
  "NT — Barkly Work Camp",
  "NT — Darwin Correctional Centre",
  "NT — Datjala Work Camp",
  // QLD
  "QLD — Arthur Gorrie Correctional Centre",
  "QLD — Borallon Training and Correctional Centre",
  "QLD — Brisbane Correctional Centre",
  "QLD — Brisbane Women's Correctional Centre",
  "QLD — Capricornia Correctional Centre",
  "QLD — Lotus Glen Correctional Centre",
  "QLD — Maryborough Correctional Centre",
  "QLD — Numinbah Correctional Centre",
  "QLD — Palen Creek Correctional Centre",
  "QLD — Southern Queensland Correctional Centre",
  "QLD — Townsville Correctional Centre",
  "QLD — Wolston Correctional Centre",
  "QLD — Woodford Correctional Centre",
  // SA
  "SA — Adelaide Pre-release Centre",
  "SA — Adelaide Remand Centre",
  "SA — Adelaide Women's Prison",
  "SA — Cadell Training Centre",
  "SA — Mobilong Prison",
  "SA — Mount Gambier Prison",
  "SA — Port Augusta Prison",
  "SA — Port Lincoln Prison",
  "SA — Yatala Labour Prison",
  // TAS
  "TAS — Hayes Prison Farm",
  "TAS — Launceston Reception Prison",
  "TAS — Mary Hutchinson Women's Prison",
  "TAS — Risdon Prison Complex",
  "TAS — Ron Barwick Minimum Security Prison",
  // VIC
  "VIC — Barwon Prison",
  "VIC — Beechworth Correctional Centre",
  "VIC — Dame Phyllis Frost Centre",
  "VIC — Dhurringile Prison",
  "VIC — Fulham Correctional Centre",
  "VIC — Hopkins Correctional Centre",
  "VIC — Langi Kal Kal Prison",
  "VIC — Loddon Prison",
  "VIC — Marngoneet Correctional Centre",
  "VIC — Melbourne Assessment Prison",
  "VIC — Metropolitan Remand Centre",
  "VIC — Middleton Prison",
  "VIC — Port Phillip Prison",
  "VIC — Ravenhall Correctional Centre",
  "VIC — Tarrengower Prison",
  // WA
  "WA — Acacia Prison",
  "WA — Albany Regional Prison",
  "WA — Bandyup Women's Prison",
  "WA — Boronia Pre-release Centre for Women",
  "WA — Broome Regional Prison",
  "WA — Bunbury Regional Prison",
  "WA — Casuarina Prison",
  "WA — Eastern Goldfields Regional Prison",
  "WA — Greenough Regional Prison",
  "WA — Hakea Prison",
  "WA — Karnet Prison Farm",
  "WA — Pardelup Prison Farm",
  "WA — Roebourne Regional Prison",
  "WA — Wandoo Reintegration Facility",
  "WA — West Kimberley Regional Prison",
  "WA — Wooroloo Prison Farm",
  "Other / Not listed",
];

function Header() {
  const navigate = useNavigate();
  const [clicks, setClicks] = useState(0);
  const handleBrandClick = (e) => {
    // Build up to 10 clicks within 4 seconds → reveal admin login.
    // Plain anchor jumps to #top on a single click; only intercept on the
    // build-up so we don't break normal scroll-to-top behaviour.
    setClicks((c) => {
      const next = c + 1;
      if (next >= 10) {
        e.preventDefault();
        navigate("/admin");
        return 0;
      }
      return next;
    });
    // Reset the counter after 4s of inactivity
    setTimeout(() => setClicks(0), 4000);
  };
  return (
    <header
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-md"
      style={{ background: "rgba(11,11,12,0.65)", borderBottom: "1px solid var(--line)" }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a
          href="#top"
          onClick={handleBrandClick}
          className="flex items-center gap-2.5 select-none"
          data-testid="brand-link"
        >
          <div
            className="w-9 h-9 rounded-md flex items-center justify-center"
            style={{ background: "var(--accent)", color: "#0b0b0c" }}
          >
            <Lock size={18} strokeWidth={2.5} />
          </div>
          <div className="leading-none">
            <div className="font-display text-2xl tracking-wide">CELLBLOCK B</div>
            <div className="text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: "var(--accent)" }}>
              Talk is Cheap
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] mt-0.5" style={{ color: "var(--muted)" }}>
              Cheapest in Australia
            </div>
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: "var(--muted)" }}>
          <a href="#how" className="hover:text-white transition" data-testid="nav-how">How it works</a>
          <a href="#pricing" className="hover:text-white transition" data-testid="nav-pricing">Pricing</a>
          <a href="#features" className="hover:text-white transition" data-testid="nav-features">Why us</a>
          <a href="#contact" className="hover:text-white transition" data-testid="nav-contact">Contact</a>
        </nav>
        <a href="#signup" className="btn-primary text-sm" data-testid="header-cta">
          Get Number <ArrowRight size={16} />
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
      <div className="absolute inset-0 bars-bg opacity-60 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium anim-up"
              style={{ border: "1px solid var(--line)", background: "rgba(245,165,36,0.08)", color: "var(--accent)" }}
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full pulse-amber" style={{ background: "var(--accent)" }} />
              AUSTRALIA'S LOWEST PRICE — YOU WON'T FIND IT CHEAPER
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-6 leading-[0.95] anim-up delay-1" data-testid="hero-title">
              CHEAPER PRISON
              <br />
              <span style={{ color: "var(--accent)" }}>CALLS.</span> SIMPLE.
            </h1>

            {/* HUGE $1/day banner — undercuts every AU dealer */}
            <div
              className="mt-7 anim-up delay-2 inline-block"
              data-testid="one-dollar-banner"
            >
              <div
                className="font-display leading-[0.9] text-[56px] sm:text-7xl lg:text-[88px]"
                style={{ color: "var(--accent)", textShadow: "0 10px 40px rgba(245,165,36,0.25)" }}
              >
                ONLY $1 <span className="text-white">A DAY.</span>
              </div>
              <div
                className="mt-3 font-display text-xl sm:text-2xl lg:text-3xl uppercase tracking-[0.04em]"
                style={{ color: "rgba(255,255,255,0.95)" }}
              >
                Cheapest deal in Australia — undercutting every other dealer out there.
              </div>
            </div>

            <p
              className="mt-6 font-display text-3xl sm:text-4xl max-w-xl leading-tight anim-up delay-2"
              data-testid="hero-subtitle"
            >
              AUSTRALIA'S CHEAPEST PRISON CALL SERVICE <span style={{ color: "var(--accent)" }}>GUARANTEED.</span>
            </p>

            <div className="mt-6 anim-up delay-3" data-testid="hero-plan-line">
              <div className="text-base sm:text-lg" style={{ color: "var(--muted)" }}>
                <span className="text-white font-semibold">One price. One plan.</span>{" "}
                $1 a day. Cancel anytime.
              </div>
              <div className="text-xs mt-1.5" style={{ color: "var(--muted)" }}>
                Unlimited inmate calls. No per-minute fees. No contracts.
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 anim-up delay-3">
              <a href="#signup" className="btn-primary" data-testid="hero-cta-primary">
                Get Your Number <ArrowRight size={18} />
              </a>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm anim-up delay-4" style={{ color: "var(--muted)" }}>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} style={{ color: "var(--accent)" }} /> Unlimited minutes</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} style={{ color: "var(--accent)" }} /> Instant activation</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} style={{ color: "var(--accent)" }} /> No setup, no app, no SIM</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} style={{ color: "var(--accent)" }} /> Cancel anytime</div>
            </div>
          </div>

          <div className="lg:col-span-5 anim-up delay-2">
            <PriceCardHero />
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceCardHero() {
  return (
    <div className="relative">
      <div className="absolute -inset-2 rounded-3xl" style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(245,165,36,0.25), transparent 70%)" }} />
      <div className="card p-7 sm:p-8 relative" data-testid="hero-price-card">
        <div className="flex items-center justify-between">
          <div className="text-xs font-mono uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
            Best Value
          </div>
          <div className="flex items-center gap-1 text-xs" style={{ color: "var(--accent)" }}>
            <Star size={14} fill="currentColor" />
            <Star size={14} fill="currentColor" />
            <Star size={14} fill="currentColor" />
            <Star size={14} fill="currentColor" />
            <Star size={14} fill="currentColor" />
          </div>
        </div>
        <div className="mt-2 font-display text-3xl">$1 A DAY · UNLIMITED</div>
        <div className="mt-5 flex items-baseline gap-2">
          <span className="font-display text-7xl" style={{ color: "var(--accent)" }}>$1</span>
          <span className="text-sm" style={{ color: "var(--muted)" }}>/ day</span>
        </div>
        <div className="mt-1 text-sm" style={{ color: "var(--muted)" }}>
          Billed <span className="text-white font-semibold">$30 every 30 days</span> — cancel anytime from your dashboard.
        </div>
        <ul className="mt-6 space-y-2.5 text-sm">
          {[
            "Only $1 a day — flat",
            "Unlimited calls & minutes",
            "Instant local landline setup",
            "All Australian prisons supported",
            "Free relocation if inmate moves",
            "Cancel anytime — no contracts",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2.5">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--accent)" }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <a
          href="#signup"
          className="btn-primary mt-7 w-full justify-center"
          data-testid="hero-card-cta"
          onClick={() => {
            window.cbbTrack && window.cbbTrack("begin_signup", { source: "hero_card", plan_name: "one_dollar_day", value: 30, currency: "AUD" });
          }}
        >
          Start — $1 A Day <ArrowRight size={18} />
        </a>
        <div className="mt-3 text-center text-xs" style={{ color: "var(--muted)" }}>
          No sign-up fee. No per-minute charges. Cancel anytime.
        </div>
      </div>
    </div>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-xl mx-auto">
          <PriceCard
            tier="ONE PRICE · ONE PLAN"
            price="1"
            period="/ day"
            note="Billed $30 every 30 days. Cancel anytime from your dashboard."
            features={[
              "Only $1 a day — flat",
              "Unlimited calls & minutes",
              "Instant local landline setup",
              "All Australian prisons supported",
              "Free number relocation if inmate moves",
              "Cancel anytime — no contracts, no fees",
            ]}
            ctaPlan="30-day"
            highlighted={true}
            badge="CHEAPEST IN AU"
            testid="price-card-30day"
          />
        </div>

        <div
          className="mt-10 max-w-3xl mx-auto rounded-2xl p-5 flex items-start gap-3 text-sm"
          style={{ background: "rgba(245,165,36,0.06)", border: "1px solid rgba(245,165,36,0.25)" }}
          data-testid="lowest-price-banner"
        >
          <ShieldCheck size={20} style={{ color: "var(--accent)" }} className="mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold text-white">Lowest Price Guarantee.</span>{" "}
            <span style={{ color: "var(--muted)" }}>
              Cellblock B is the cheapest prison call provider in Australia. If you find it cheaper anywhere
              else, we'll match it. Simple as that.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceCard({ tier, price, period, note, features, ctaPlan, highlighted, badge, testid }) {
  return (
    <div
      className={`card p-7 sm:p-8 relative transition ${highlighted ? "" : "hover:-translate-y-0.5"}`}
      style={highlighted ? { borderColor: "var(--accent)", boxShadow: "0 0 0 1px var(--accent), 0 30px 60px -30px rgba(245,165,36,0.3)" } : {}}
      data-testid={testid}
    >
      {badge && (
        <div
          className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[10px] font-bold tracking-widest"
          style={{ background: "var(--accent)", color: "#0b0b0c" }}
        >
          {badge}
        </div>
      )}
      <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>{tier}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-display text-2xl mt-2" style={{ color: "var(--muted)" }}>$</span>
        <span className="font-display text-7xl leading-none" style={{ color: highlighted ? "var(--accent)" : "var(--text)" }}>{price}</span>
        <span className="text-sm" style={{ color: "var(--muted)" }}>{period}</span>
      </div>
      <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>{note}</p>
      <ul className="mt-6 space-y-2.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--accent)" }} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <a
        href="#signup"
        className={highlighted ? "btn-primary mt-7 w-full justify-center" : "btn-ghost mt-7 w-full justify-center"}
        data-testid={`${testid}-cta`}
        onClick={() => {
          const planMeta = { plan_name: "one_dollar_day", value: 30 };
          window.cbbTrack && window.cbbTrack("select_plan", { ...planMeta, currency: "AUD", source: "pricing_card" });
          window.cbbTrack && window.cbbTrack("begin_signup", { ...planMeta, currency: "AUD", source: "pricing_card" });
        }}
      >
        Start — $1 a day <ArrowRight size={16} />
      </a>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", title: "Sign Up", body: "Tell us a few details about you and your inmate." },
    { n: "02", title: "Choose a Prison", body: "Pick the facility — we cover every prison in Australia." },
    { n: "03", title: "One Flat Price", body: "$1 a day. Billed $30 every 30 days. Unlimited calls, cancel anytime." },
    { n: "04", title: "Get Your Number", body: "Your local landline is delivered instantly to your inbox." },
  ];
  return (
    <section id="how" className="py-24 relative" style={{ background: "var(--bg-2)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          eyebrow="How It Works"
          title="FOUR STEPS. UNDER A MINUTE."
          subtitle="No paperwork. No phone calls. No queues. Just a number that connects you."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {steps.map((s, i) => (
            <div key={s.n} className="card p-6 relative overflow-hidden group" data-testid={`step-${i + 1}`}>
              <div
                className="font-display text-7xl absolute -top-2 -right-2 select-none"
                style={{ color: "rgba(245,165,36,0.10)" }}
              >
                {s.n}
              </div>
              <div className="font-mono text-xs tracking-widest" style={{ color: "var(--accent)" }}>STEP {s.n}</div>
              <div className="font-display text-2xl mt-3">{s.title}</div>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { icon: InfinityIcon, title: "Unlimited Minutes", body: "No per-minute charges, ever. Talk as long as you need." },
    { icon: Zap, title: "Instant Setup", body: "Number delivered to your inbox the moment you sign up." },
    { icon: MapPin, title: "Every Australian Prison", body: "Local landlines for every facility, every state, every territory." },
    { icon: Smartphone, title: "Easy Mobile App", body: "iOS & Android. Scan your QR code and start receiving calls." },
    { icon: Repeat, title: "Free Relocation", body: "Inmate transferred? We'll provide a replacement number free." },
    { icon: ShieldCheck, title: "Built for the Rules", body: "Calls terminate to our app — no diversion, no forwarding." },
  ];
  return (
    <section id="features" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          eyebrow="Why Cellblock B"
          title="MORE TALK. LESS MONEY."
          subtitle="Cut prison call costs by up to 90% — and never worry about minutes again."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {items.map((it, i) => (
            <div key={it.title} className="card p-6 hover:-translate-y-1 transition" data-testid={`feature-${i}`}>
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ background: "rgba(245,165,36,0.10)", color: "var(--accent)", border: "1px solid rgba(245,165,36,0.25)" }}
              >
                <it.icon size={20} />
              </div>
              <div className="font-display text-xl mt-4">{it.title}</div>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// SEOExplainer — long-form intent-keyword content. Helps Google understand
// what we sell and rank for searches like "how to make prison calls cheaper
// in Australia", "prison call forwarding", "cheapest inmate calls" etc.
// Written for humans first; keyword density is incidental, not stuffed.
// ============================================================================
function SEOExplainer() {
  return (
    <section className="py-20 border-t" style={{ borderColor: "var(--line)", background: "var(--bg-2)" }} id="learn">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>
          The Basics
        </div>
        <h2 className="font-display text-3xl sm:text-4xl mt-3">How Prison Call Forwarding Works In Australia</h2>
        <div className="mt-6 space-y-5 text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
          <p>
            Inmate phone systems in Australian correctional centres charge significantly more
            per minute when a prisoner calls a mobile number than when they call a local
            landline. Cellblock B closes that gap. We give you a <strong>local Australian
            landline number</strong> tied to your account, and we forward every incoming call
            on that line straight to your mobile.
          </p>
          <p>
            That means your loved one dials what looks like an ordinary local number from inside
            the prison phone system — paying the cheapest possible rate — and you pick up on the
            mobile you already carry. No app to install on the prison side. No new SIM. No special
            handset.
          </p>
        </div>

        <h3 className="font-display text-2xl sm:text-3xl mt-12">Save Money On Prison Phone Calls</h3>
        <div className="mt-4 space-y-5 text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
          <p>
            Customers using Cellblock B typically reduce their prison phone bills by{" "}
            <strong>up to 90%</strong> compared to paying expensive per-minute mobile call rates from
            the prison phone system. One flat plan — <strong>$1 a day</strong>, billed{" "}
            <strong>$30 every 30 days</strong>, unlimited inbound inmate calls. No per-minute
            surcharges from us. No surprise bills.
          </p>
        </div>

        <h3 className="font-display text-2xl sm:text-3xl mt-12">Instant Activation — No Setup, No App, No SIM</h3>
        <div className="mt-4 space-y-5 text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
          <p>
            There is <strong>nothing to install</strong>. No app on your phone. No new SIM card.
            No new handset. No technical setup. The moment your payment clears, your local
            Australian landline number is provisioned automatically and emailed straight to you —
            usually within a couple of minutes. Hand it to your loved one on the next call.
            That's it. <strong>Same-day activation, Australia-wide.</strong>
          </p>
          <p>
            Cellblock B works with correctional centres in every Australian state and territory
            — including{" "}
            <Link to="/qld-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>Queensland</Link>,{" "}
            <Link to="/nsw-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>New South Wales</Link>,{" "}
            <Link to="/vic-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>Victoria</Link>,{" "}
            <Link to="/wa-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>Western Australia</Link>,{" "}
            <Link to="/sa-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>South Australia</Link>,{" "}
            <Link to="/tas-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>Tasmania</Link>,{" "}
            <Link to="/act-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>the ACT</Link>{" "}
            and the{" "}
            <Link to="/nt-prison-calls" className="underline hover:text-white transition" style={{ color: "var(--accent)" }}>Northern Territory</Link>.
          </p>
        </div>

        <h3 className="font-display text-2xl sm:text-3xl mt-12">Cheapest Inmate Calls In Australia — Guaranteed</h3>
        <div className="mt-4 space-y-5 text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
          <p>
            We've benchmarked our pricing against every Australian prison-call forwarding service
            we could find. At a flat <strong>$1 a day</strong> ($30 every 30 days), Cellblock B is
            the cheapest inmate-call provider in the country — undercutting every other dealer.
            If you find a comparable Australian service for less, we'll match the price.
          </p>
        </div>

        <h3 className="font-display text-2xl sm:text-3xl mt-12">Free Relocation If The Inmate Moves</h3>
        <div className="mt-4 space-y-5 text-base leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
          <p>
            Inmates transfer between correctional facilities more often than families expect.
            When that happens, we provide a replacement local landline number for the new
            location at <strong>no extra cost</strong> — your subscription keeps running, the new
            number takes over, no disruption to the inmate's call routine.
          </p>
        </div>
      </div>
    </section>
  );
}

function SignupForm() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    mobile: "",
    prison: "",
    plan: "30-day",
    notes: "",
    agreed_no_chargeback: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Listen for plan selection from pricing cards
  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, plan: e.detail }));
    window.addEventListener("cbb:select-plan", handler);
    return () => window.removeEventListener("cbb:select-plan", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once subscription
  }, []);

  // Referral program fully removed Feb 2026.

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const PLAN_META = {
    "30-day": { name: "one_dollar_day", value: 30, label: "$1 a day" },
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.mobile || !form.prison) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!form.agreed_no_chargeback) {
      toast.error("Please agree to the no-chargeback terms to continue.");
      return;
    }
    setSubmitting(true);
    try {
      const meta = PLAN_META[form.plan] || PLAN_META["30-day"];
      const planMeta = { plan_name: meta.name, value: meta.value };
      window.cbbTrack && window.cbbTrack("sign_up", { ...planMeta, currency: "AUD" });

      // 1) Create signup
      const resp = await axios.post(`${API}/signup`, form);
      const signupId = resp.data.id;
      try { window.sessionStorage.setItem("cbb_signup_id", signupId); } catch (e) { /* private-mode */ }
      try { window.sessionStorage.setItem("cbb_signup_plan", form.plan); } catch (e) { /* private-mode */ }
      window.cbbTrack && window.cbbTrack("begin_checkout", {
        ...planMeta,
        currency: "AUD",
        transaction_id: signupId,
      });
      // 2) Create Stripe checkout session
      const sessionResp = await axios.post(`${API}/checkout/session`, {
        signup_id: signupId,
        origin_url: window.location.origin,
      });
      // 3) Redirect to Stripe
      if (sessionResp.data?.url) {
        window.location.href = sessionResp.data.url;
        return;
      }
      toast.error("Could not start checkout. Please try again.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="card p-6 sm:p-8" data-testid="signup-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">
          <label className="label">Full name *</label>
          <input
            className="field"
            value={form.full_name}
            onChange={(e) => update("full_name", e.target.value)}
            placeholder="Jane Doe"
            required
            data-testid="signup-name"
          />
        </div>
        <div>
          <label className="label">Email *</label>
          <input
            type="email"
            className="field"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@email.com"
            required
            data-testid="signup-email"
          />
        </div>
        <div>
          <label className="label">Mobile *</label>
          <input
            className="field"
            value={form.mobile}
            onChange={(e) => update("mobile", e.target.value)}
            placeholder="04XX XXX XXX"
            required
            data-testid="signup-mobile"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="label">Prison *</label>
          <select
            className="field"
            value={form.prison}
            onChange={(e) => update("prison", e.target.value)}
            required
            data-testid="signup-prison"
          >
            <option value="">Select a prison...</option>
            {PRISONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Your plan</label>
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ background: "rgba(245,165,36,0.06)", border: "1px solid rgba(245,165,36,0.3)" }}
            data-testid="plan-summary"
          >
            <CheckCircle2 size={22} style={{ color: "var(--accent)" }} className="mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="text-white font-semibold">$1 a day · unlimited calls</div>
              <div style={{ color: "var(--muted)" }}>
                Billed $30 every 30 days. Cancel anytime from your dashboard — no contracts, no fees.
              </div>
            </div>
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="label">Notes (optional)</label>
          <textarea
            className="field"
            rows={3}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Anything we should know?"
            data-testid="signup-notes"
          />
        </div>
      </div>

      <div
        className="mt-6 p-4 rounded-xl"
        style={{ background: "rgba(229,72,77,0.06)", border: "1px solid rgba(229,72,77,0.35)" }}
      >
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.agreed_no_chargeback}
            onChange={(e) => update("agreed_no_chargeback", e.target.checked)}
            className="mt-1 w-5 h-5 flex-shrink-0 cursor-pointer accent-amber-500"
            style={{ accentColor: "var(--accent)" }}
            required
            data-testid="signup-agreement"
          />
          <span className="text-xs leading-relaxed" style={{ color: "var(--muted)" }}>
            <span className="text-white font-semibold">No chargeback agreement.</span> I acknowledge
            that I am entering into a binding recurring subscription with Cellblock B in exchange
            for a local landline number that allows my loved one to call me at local-call rates. I
            understand my card will be{" "}
            <span className="text-white font-semibold">automatically charged $30 every 30 days
            (that's just $1 a day)</span>{" "}
            until I cancel. I agree <span className="text-white font-semibold">not to dispute,
            reverse, or request a chargeback</span> on any payment with my bank or card issuer. The
            service is delivered electronically and the number is provisioned immediately. I can
            cancel anytime by emailing hello@cellblockb.com.au.
          </span>
        </label>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary mt-6 w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="signup-submit"
      >
        {submitting
          ? "Redirecting to checkout..."
          : "Start — $1 a day. Cancel anytime."} <ArrowRight size={18} />
      </button>
      <div className="mt-3 text-center text-xs" style={{ color: "var(--muted)" }}>
        <Lock size={12} className="inline mr-1.5 -mt-0.5" />
        Secure checkout via Stripe. Your details are private.
      </div>
    </form>
  );
}

function PlanRadio({ checked, onClick, title, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left p-4 rounded-xl transition"
      style={{
        border: `1px solid ${checked ? "var(--accent)" : "var(--line)"}`,
        background: checked ? "rgba(245,165,36,0.07)" : "#0f0f12",
        boxShadow: checked ? "0 0 0 3px rgba(245,165,36,0.15)" : "none",
      }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span
          className="w-4 h-4 rounded-full flex items-center justify-center"
          style={{ border: `1px solid ${checked ? "var(--accent)" : "var(--line)"}` }}
        >
          {checked && <span className="w-2 h-2 rounded-full" style={{ background: "var(--accent)" }} />}
        </span>
        <span className="font-display text-lg">{title}</span>
      </div>
      <div className="mt-1 text-xs" style={{ color: "var(--muted)" }}>{sub}</div>
    </button>
  );
}

function FeatureBullet({ icon: Icon, title, sub }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5"
        style={{ background: "rgba(245,165,36,0.10)", border: "1px solid rgba(245,165,36,0.35)" }}
      >
        <Icon size={14} style={{ color: "var(--accent)" }} strokeWidth={2.5} />
      </span>
      <div className="flex-1 leading-tight">
        <div className="font-display text-sm tracking-wide text-white">{title}</div>
        <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>{sub}</div>
      </div>
    </li>
  );
}

function SignupSection() {
  return (
    <section id="signup" className="py-24 relative" style={{ background: "var(--bg-2)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHead
              align="left"
              eyebrow="Sign Up"
              title="GET YOUR NUMBER HERE."
              subtitle="A local landline near your prison, forwarded straight to your mobile. Set up in minutes."
            />
            <ul className="mt-8 space-y-4 text-sm">
              <FeatureBullet icon={InfinityIcon} title="UNLIMITED TALK"           sub="Never pay for minutes again" />
              <FeatureBullet icon={MapPin}       title="INSTANT LOCAL LANDLINE"  sub="Every Australian prison" />
              <FeatureBullet icon={ShieldCheck}  title="STOP GETTING RIPPED OFF" sub="Start saving your money" />
              <FeatureBullet icon={Star}         title="LOWEST PRICE IN AU"      sub="Guaranteed" />
              <FeatureBullet icon={Repeat}       title="NO CONTRACTS"            sub="Cancel anytime" />
            </ul>
          </div>
          <div className="lg:col-span-7">
            <SignupForm />
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      // GA4 / GTM — lead captured
      window.cbbTrack && window.cbbTrack("generate_lead", {
        form_name: "contact_form",
        currency: "AUD",
        value: 10,  // nominal lead value for ad bidding
      });
      toast.success("Message sent. We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <SectionHead
          eyebrow="Contact"
          title="ANY QUESTIONS?"
          subtitle="Drop us a message. We answer fast."
        />
        <form onSubmit={submit} className="card p-6 sm:p-8 mt-10" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="label">Name *</label>
              <input
                className="field"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                className="field"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="contact-email"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Phone</label>
              <input
                className="field"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="contact-phone"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Message *</label>
              <textarea
                className="field"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                data-testid="contact-message"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary mt-6 disabled:opacity-60"
            data-testid="contact-submit"
          >
            {submitting ? "Sending..." : "Send Message"} <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t" style={{ borderColor: "var(--line)", background: "#08080a" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div>
            <div className="flex items-center gap-2.5">
              <div
                className="w-9 h-9 rounded-md flex items-center justify-center"
                style={{ background: "var(--accent)", color: "#0b0b0c" }}
              >
                <Lock size={18} strokeWidth={2.5} />
              </div>
              <div className="font-display text-2xl">CELLBLOCK B</div>
            </div>
            <p className="mt-4 text-sm max-w-sm" style={{ color: "var(--muted)" }}>
              Australia's cheapest prison call provider. Local landlines for every prison —
              unlimited calls forwarded to your mobile.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "var(--muted)" }}>By state</div>
            <ul className="space-y-2 text-sm" data-testid="footer-state-links">
              <li><Link to="/qld-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Queensland</Link></li>
              <li><Link to="/nsw-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>New South Wales</Link></li>
              <li><Link to="/vic-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Victoria</Link></li>
              <li><Link to="/wa-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Western Australia</Link></li>
              <li><Link to="/sa-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>South Australia</Link></li>
              <li><Link to="/tas-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Tasmania</Link></li>
              <li><Link to="/act-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Australian Capital Territory</Link></li>
              <li><Link to="/nt-prison-calls" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Northern Territory</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: "var(--muted)" }}>Quick Links</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#how" className="hover:text-white transition" style={{ color: "var(--muted)" }}>How it works</a></li>
              <li><a href="#pricing" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Pricing</a></li>
              <li><a href="#signup" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Sign up</a></li>
              <li><a href="#contact" className="hover:text-white transition" style={{ color: "var(--muted)" }}>Contact</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t flex flex-wrap items-center justify-between gap-3 text-xs" style={{ borderColor: "var(--line)", color: "var(--muted)" }}>
          <div>© {new Date().getFullYear()} Cellblock B. All Rights Reserved.</div>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy-policy" className="hover:text-white transition" data-testid="footer-link-privacy">Privacy Policy</Link>
            <Link to="/refund-policy" className="hover:text-white transition" data-testid="footer-link-refund">Refund Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition" data-testid="footer-link-terms">Terms &amp; Conditions</Link>
            <span className="font-mono">cellblockb.com.au</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function SectionHead({ eyebrow, title, subtitle, align = "center" }) {
  return (
    <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-2xl"}>
      <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>
        {eyebrow}
      </div>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">{title}</h2>
      {subtitle && <p className="mt-4 text-base sm:text-lg" style={{ color: "var(--muted)" }}>{subtitle}</p>}
    </div>
  );
}

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How does Cellblock B work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cellblock B provides you with a local Australian landline number that the inmate dials from inside prison. We bridge the call to your mobile, so you pay one low monthly subscription instead of expensive per-minute mobile call rates.",
      },
    },
    {
      "@type": "Question",
      "name": "How much does Cellblock B cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "One plan, one price: $1 a day — billed $30 every 30 days. It auto-renews and you can cancel anytime. Unlimited inmate calls are included.",
      },
    },
    {
      "@type": "Question",
      "name": "How can I make prison calls cheaper in Australia?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The cheapest way to receive Australian prison calls is to route them through a local landline number. Inmates pay the local-landline rate from the prison phone system instead of expensive mobile rates. Cellblock B sets this up for you for a flat $1 a day — billed $30 every 30 days — unlimited calls, no per-minute fees.",
      },
    },
    {
      "@type": "Question",
      "name": "What is prison call forwarding?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Prison call forwarding is a service where you receive a local Australian landline number, and any call to that number is automatically forwarded to your mobile phone. Inmates dial it as a local call from the prison phone, so the per-minute charge inside the prison system is far cheaper than a direct call to a mobile.",
      },
    },
    {
      "@type": "Question",
      "name": "Which Australian prisons does Cellblock B work with?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cellblock B works with correctional centres across all Australian states and territories that allow inmates to dial out to local landline numbers — including facilities in NSW, Queensland, Victoria, Western Australia, South Australia, Tasmania, ACT and Northern Territory.",
      },
    },
    {
      "@type": "Question",
      "name": "How fast is the prison number setup?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Instant activation, no setup required. There is no app to install, no new SIM card, and no special handset — your local Australian landline number is provisioned automatically within a few minutes of payment and emailed straight to you. You can give it to your loved one on their very next call. Same-day activation, Australia-wide.",
      },
    },
    {
      "@type": "Question",
      "name": "What happens if the inmate is transferred?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "If the inmate is transferred to another correctional facility, we provide a replacement local number where available at no extra cost. This is included in every Cellblock B plan.",
      },
    },
    {
      "@type": "Question",
      "name": "Are inmate calls really unlimited?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Our single $1-a-day plan ($30 every 30 days) includes unlimited inbound inmate calls. There are no per-minute fees and no cap on call duration from the Cellblock B side.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I cancel anytime?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Cancel at any time from your billing portal. Your subscription will not auto-renew. A 10-day money-back guarantee also applies — see our Refund Policy.",
      },
    },
    {
      "@type": "Question",
      "name": "Is Cellblock B endorsed by Australian prisons?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Cellblock B is independent and is not endorsed by, approved by, or affiliated with any correctional centre or government department.",
      },
    },
  ],
};

const Home = () => (
  <div className="App relative">
    <Header />
    <Hero />
    <Pricing />
    <HowItWorks />
    <Features />
    <SEOExplainer />
    <SignupSection />
    <ContactSection />
    <Footer />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}
    />
  </div>
);

function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const maxAttempts = 8;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const ps = res.data?.payment_status;
        const st = res.data?.status;
        setAttempts(n);
        if (ps === "paid") {
          setStatus("paid");
          // GA4 / GTM — purchase confirmed (fires ONCE per session_id load)
          let plan = "30-day";
          try { plan = window.sessionStorage.getItem("cbb_signup_plan") || "30-day"; } catch (_e) { /* private-mode */ }
          const PLAN_META = {
            "30-day": { plan_name: "one_dollar_day", item_id: "one_dollar_day", value: 30, item_name: "Cellblock B — $1 a Day Unlimited" },
            // legacy (surviving subscribers)
            "14-day":   { plan_name: "fourteen_day_14", item_id: "fourteen_day_14", value: 14, item_name: "Cellblock B 14-Day Plan (legacy)" },
            "60-day":   { plan_name: "sixty_day_60",    item_id: "sixty_day_60",    value: 60, item_name: "Cellblock B 60-Day Plan (legacy)" },
            "2-weekly": { plan_name: "fourteen_day_14", item_id: "fourteen_day_14", value: 14, item_name: "Cellblock B 14-Day Plan (legacy)" },
            "monthly":  { plan_name: "monthly_25",      item_id: "monthly_25",      value: 25, item_name: "Cellblock B Monthly Plan (legacy)" },
            "5-weekly": { plan_name: "five_weekly_35",  item_id: "five_weekly_35",  value: 35, item_name: "Cellblock B 5-Weekly Plan (legacy)" },
            "3-month":  { plan_name: "three_month_70", item_id: "three_month_70",  value: 70, item_name: "Cellblock B 3-Month Plan (legacy)" },
          };
          const planMeta = PLAN_META[plan] || PLAN_META["30-day"];
          window.cbbTrack && window.cbbTrack("purchase", {
            transaction_id: sessionId,
            value: planMeta.value,
            currency: "AUD",
            items: [{
              item_id: planMeta.item_id,
              item_name: planMeta.item_name,
              price: planMeta.value,
              quantity: 1,
            }],
          });
          return;
        }
        if (st === "expired") {
          setStatus("expired");
          return;
        }
        if (n >= maxAttempts) {
          setStatus("error");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (n >= maxAttempts) {
          setStatus("error");
          return;
        }
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="card p-10 max-w-lg w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin" size={36} style={{ color: "var(--accent)" }} />
            <div className="font-display text-3xl mt-5">CONFIRMING PAYMENT…</div>
            <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
              Hang tight — we're verifying your payment with Stripe. (Attempt {attempts})
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div
              className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: "rgba(245,165,36,0.12)", color: "var(--accent)" }}
            >
              <CheckCircle2 size={28} />
            </div>
            <div className="font-display text-4xl mt-5" data-testid="payment-success-title">YOU'RE IN.</div>
            <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
              Payment received. Your Cellblock B number is being provisioned and will land in your inbox
              within minutes.
            </p>
            <Link to="/" className="btn-primary mt-7 inline-flex" data-testid="payment-success-home">
              Back to Home <ArrowRight size={18} />
            </Link>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="mx-auto" size={36} style={{ color: "var(--danger)" }} />
            <div className="font-display text-3xl mt-5">SESSION EXPIRED</div>
            <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
              Your checkout session expired. Please try signing up again.
            </p>
            <Link to="/#signup" className="btn-primary mt-7 inline-flex">
              Try Again <ArrowRight size={18} />
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto" size={36} style={{ color: "var(--danger)" }} />
            <div className="font-display text-3xl mt-5">CAN'T CONFIRM RIGHT NOW</div>
            <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
              We couldn't confirm your payment automatically. If you completed checkout, check your
              email — your number is on its way. Otherwise, please contact us.
            </p>
            <Link to="/#contact" className="btn-ghost mt-7 inline-flex">
              Contact Us <ArrowRight size={18} />
            </Link>
          </>
        )}
      </div>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

function PaymentCancelledPage() {
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="card p-10 max-w-lg w-full text-center" data-testid="payment-cancelled-page">
        <XCircle className="mx-auto" size={36} style={{ color: "var(--danger)" }} />
        <div className="font-display text-3xl mt-5">PAYMENT CANCELLED</div>
        <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
          No worries — you weren't charged. Your details are saved. You can complete checkout
          whenever you're ready.
        </p>
        <Link to="/#signup" className="btn-primary mt-7 inline-flex" data-testid="payment-cancelled-retry">
          Try Again <ArrowRight size={18} />
        </Link>
      </div>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

function ManagePage() {
  const { signupId } = useParams();
  const [status, setStatus] = useState("loading"); // loading | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!signupId) {
      setStatus("error");
      setError("Missing subscription id");
      return;
    }
    (async () => {
      try {
        const r = await axios.post(`${API}/billing/portal-session`, {
          signup_id: signupId,
          return_url: window.location.origin,
        });
        if (r.data?.url) {
          window.location.href = r.data.url;
          return;
        }
        setStatus("error");
        setError("Couldn't open the customer portal.");
      } catch (e) {
        setStatus("error");
        setError(e?.response?.data?.detail || "Could not open portal. Please contact support.");
      }
    })();
  }, [signupId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="card p-10 max-w-md w-full text-center" data-testid="manage-page">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto animate-spin" size={36} style={{ color: "var(--accent)" }} />
            <div className="font-display text-2xl mt-5">OPENING PORTAL…</div>
            <p className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
              Redirecting you to Stripe to manage your subscription.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto" size={36} style={{ color: "var(--danger)" }} />
            <div className="font-display text-2xl mt-5">CAN'T OPEN PORTAL</div>
            <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>{error}</p>
            <Link to="/" className="btn-ghost mt-7 inline-flex">
              Back to home <ArrowRight size={18} />
            </Link>
          </>
        )}
      </div>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

function AdminLogin({ onAuthed }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password: pw });
      const token = r.data?.token;
      if (!token) throw new Error("No token");
      try { window.sessionStorage.setItem("cbb_admin_token", token); } catch (e) { /* private-mode / storage disabled */ }
      onAuthed(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <form onSubmit={submit} className="card p-8 max-w-sm w-full" data-testid="admin-login">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--accent)", color: "#0b0b0c" }}>
            <Lock size={18} strokeWidth={2.5} />
          </div>
          <div className="font-display text-xl">RESTRICTED</div>
        </div>
        <label className="label">Admin password</label>
        <div className="relative">
          <input
            type={show ? "text" : "password"}
            className="field pr-10"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            autoComplete="off"
            data-testid="admin-pw"
          />
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
            tabIndex={-1}
            aria-label="Toggle password visibility"
          >
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <button
          type="submit"
          disabled={busy || !pw}
          className="btn-primary w-full justify-center mt-5 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {busy ? "Checking…" : "Sign in"} <ArrowRight size={16} />
        </button>
        <Link to="/" className="block text-center mt-4 text-xs opacity-60 hover:opacity-100">
          Back to site
        </Link>
      </form>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

function AdminDashboard({ token, onLogout }) {
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [twilioBalance, setTwilioBalance] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [busyId, setBusyId] = useState("");

  const headers = { "X-Admin-Token": token };

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, sg, nb, cl, tb] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/signups`, { headers }),
        axios.get(`${API}/twilio/numbers`, { headers }),
        axios.get(`${API}/twilio/call-logs?limit=20`, { headers }),
        axios.get(`${API}/admin/twilio/balance`, { headers }).catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setSignups(sg.data);
      setNumbers(nb.data);
      setCallLogs(cl.data);
      setTwilioBalance(tb.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Live-refresh every 30s
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const cancel = async (signupId) => {
    if (!window.confirm("Cancel this subscription? This will release the Twilio number immediately.")) return;
    setBusyId(signupId);
    try {
      await axios.post(`${API}/admin/cancel/${signupId}`, null, { headers, params: { reason: "admin_ui" } });
      toast.success("Subscription cancelled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusyId("");
    }
  };

  const refund = async (signupId) => {
    const alsoCancel = window.confirm(
      "REFUND THE LATEST CHARGE?\n\nPress OK to refund AND cancel the subscription (full money back).\nPress Cancel to only refund the charge but keep the sub running.",
    );
    // window.confirm returns true on OK, false on Cancel — but they both mean
    // 'go ahead', they just differ on the also_cancel flag. We use a second
    // prompt to actually abort.
    if (!window.confirm(alsoCancel
      ? "Confirm: refund LAST CHARGE and CANCEL subscription?"
      : "Confirm: refund LAST CHARGE only (sub keeps running)?"
    )) return;
    setBusyId(signupId);
    try {
      const r = await axios.post(
        `${API}/admin/refund/${signupId}`,
        null,
        { headers, params: { also_cancel: alsoCancel } },
      );
      toast.success(`Refunded $${r.data.refund.amount.toFixed(2)} ${r.data.refund.currency.toUpperCase()}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setBusyId("");
    }
  };

  const provision = async (signupId) => {
    setBusyId(signupId);
    try {
      await axios.post(`${API}/admin/provision/${signupId}`, null, { headers });
      toast.success("Provisioning triggered");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provision failed");
    } finally {
      setBusyId("");
    }
  };

  const resendEmail = async (signupId) => {
    setBusyId(signupId);
    try {
      const r = await axios.post(`${API}/admin/resend-email/${signupId}`, null, { headers });
      toast.success(`Welcome email re-sent to ${r.data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setBusyId("");
    }
  };

  const fullTwilioSweep = async () => {
    if (!window.confirm(
      "Run Twilio Sweep?\n\nThis releases EVERY Twilio number that isn't tied to an active paid subscription " +
      "(orphans, cancelled subs, and any previous silent-fail release retries). Safe to run repeatedly.",
    )) return;
    setBusyId("sweep");
    try {
      const r = await axios.post(`${API}/admin/twilio/full-sweep`, null, { headers, timeout: 120000 });
      const d = r.data;
      const released = (d.released_orphan?.length || 0) + (d.released_dead_signup?.length || 0) + (d.released_db_says_released?.length || 0);
      toast.success(
        `Sweep complete: ${released} released, ${d.kept_active?.length || 0} active kept, ${d.errors?.length || 0} errors.`,
        { duration: 8000 },
      );
      console.log("Twilio sweep report:", d);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sweep failed — check Twilio auth.");
    } finally {
      setBusyId("");
    }
  };

  const clearTestData = async () => {
    setBusyId("cleanup");
    try {
      // Step 1: dry run to show what would be deleted
      const dry = await axios.post(`${API}/admin/cleanup/test-data?dry_run=true`, null, { headers, timeout: 60000 });
      const d = dry.data;
      const preview =
        `Dry run preview — wipe test data?\n\n` +
        `• Test signups (no live number): ${d.will_delete_signups}\n` +
        `• Matching contacts: ${d.matching_contacts}\n` +
        `• Released Twilio number records: ${d.released_twilio_numbers}\n` +
        `• Stripe events > 7 days old: ${d.old_stripe_events}\n` +
        `• Skipped (have active Twilio number): ${d.skipped_signups_with_number}\n\n` +
        `Continue and DELETE everything above? (Active customers are protected.)`;
      if (!window.confirm(preview)) {
        toast.info("Cleanup cancelled.");
        return;
      }
      // Step 2: actual delete
      const r = await axios.post(`${API}/admin/cleanup/test-data?dry_run=false`, null, { headers, timeout: 120000 });
      const del = r.data.deleted || {};
      const total = Object.values(del).reduce((a, b) => a + (b || 0), 0);
      toast.success(`Test data cleared — ${total} records deleted.`, { duration: 8000 });
      console.log("Cleanup report:", r.data);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="min-h-screen px-5 py-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "var(--accent)", color: "#0b0b0c" }}>
              <Lock size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-2xl">CELLBLOCK B / ADMIN</div>
              <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
                Restricted area
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fullTwilioSweep}
              disabled={busyId === "sweep"}
              className="btn-secondary"
              title="Bidirectional Twilio ↔ DB reconcile. Releases orphan numbers."
              data-testid="admin-twilio-sweep"
            >
              {busyId === "sweep" ? "Sweeping…" : "🧹 Twilio Sweep"}
            </button>
            <button
              onClick={clearTestData}
              disabled={busyId === "cleanup"}
              className="btn-secondary"
              title="Wipe test signups, test contacts, old Stripe events, and released Twilio number records. Active customers protected."
              data-testid="admin-cleanup-test-data"
            >
              {busyId === "cleanup" ? "Cleaning…" : "🗑️ Clear Test Data"}
            </button>
            <Link
              to="/admin/analytics-debug"
              className="btn-secondary"
              title="View the last 50 dataLayer events fired in this browser — verifies GTM/GA4 tracking without GTM Preview mode."
              data-testid="admin-analytics-debug-link"
            >
              📈 Analytics Debug
            </Link>
            <button onClick={load} className="btn-secondary" data-testid="admin-refresh">
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={onLogout} className="btn-secondary" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {stats && (
          <>
            {/* Twilio Balance — top widget so you never get caught short on funds */}
            {twilioBalance && (
              <div className="mb-3 text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Twilio Account</div>
            )}
            {twilioBalance && (
              <div
                className="card p-5 mb-6 flex flex-wrap items-center justify-between gap-4"
                data-testid="admin-twilio-balance-card"
                style={{
                  border: twilioBalance.severity === "critical"
                    ? "1px solid rgba(239,68,68,0.55)"
                    : twilioBalance.severity === "warning"
                      ? "1px solid rgba(245,165,36,0.55)"
                      : "1px solid var(--line)",
                  background: twilioBalance.severity === "critical"
                    ? "rgba(239,68,68,0.07)"
                    : twilioBalance.severity === "warning"
                      ? "rgba(245,165,36,0.07)"
                      : "transparent",
                }}
              >
                <div className="flex items-baseline gap-2">
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Twilio Balance</div>
                  <div
                    className="font-display text-2xl"
                    data-testid="twilio-balance-value"
                    style={{ color: twilioBalance.severity === "critical" ? "#ef4444" : "var(--accent)" }}
                  >
                    ${Number(twilioBalance.balance).toFixed(2)} {twilioBalance.currency}
                  </div>
                  {twilioBalance.currency !== "AUD" && (
                    <div className="text-xs" style={{ color: "var(--muted)" }}>
                      (~A${Number(twilioBalance.balance_aud_est).toFixed(2)})
                    </div>
                  )}
                </div>
                <div className="text-xs flex flex-wrap gap-x-5 gap-y-1" style={{ color: "var(--muted)" }}>
                  <span>📞 <strong style={{ color: "var(--text)" }}>{twilioBalance.twilio_numbers_owned}</strong> active number{twilioBalance.twilio_numbers_owned === 1 ? "" : "s"}</span>
                  <span>💸 ~A${Number(twilioBalance.monthly_cost_aud_est).toFixed(2)} / month</span>
                  <span>
                    ⏱ Runway:&nbsp;
                    <strong
                      style={{
                        color: twilioBalance.severity === "critical"
                          ? "#ef4444"
                          : twilioBalance.severity === "warning"
                            ? "var(--accent)"
                            : "var(--text)",
                      }}
                    >
                      {twilioBalance.days_runway_est == null ? "∞" : `${twilioBalance.days_runway_est.toFixed(0)} days`}
                    </strong>
                  </span>
                </div>
                <a
                  href="https://console.twilio.com/us1/billing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-xs"
                  data-testid="twilio-billing-link"
                >
                  Top up / Auto-recharge →
                </a>
              </div>
            )}

            {/* Financials — top row */}
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Financials (AUD)</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-financials">
              <Stat label="MRR" value={fmtAud(stats.mrr_aud)} accent />
              <Stat label="ARR (12×MRR)" value={fmtAud(stats.arr_aud)} />
              <Stat label="Twilio cost / mo" value={fmtAud(stats.twilio_monthly_cost_aud)} />
              <Stat label="Net / mo" value={fmtAud(stats.net_monthly_aud)} accent={stats.net_monthly_aud > 0} />
              <Stat label="Revenue today" value={fmtAud(stats.revenue_today_aud)} />
              <Stat label="Revenue 7d" value={fmtAud(stats.revenue_last_7d_aud)} />
              <Stat label="Revenue 30d" value={fmtAud(stats.revenue_last_30d_aud)} />
              <Stat label="Lifetime gross" value={fmtAud(stats.lifetime_revenue_aud)} />
            </div>
            {/* Subscriptions */}
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Subscriptions</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="admin-stats">
              <Stat label="Active subs" value={stats.signups_active} accent />
              <Stat label="Past due" value={stats.signups_past_due} />
              <Stat label="Paid total" value={stats.signups_paid} />
              <Stat label="Pending" value={stats.signups_pending_payment} />
              <Stat label="Cancelled" value={stats.signups_cancelled} />
              <Stat label="Signups today" value={stats.signups_today} />
              <Stat label="Signups 7d" value={stats.signups_last_7d} />
              <Stat label="Signups 30d" value={stats.signups_last_30d} />
            </div>
            {/* Twilio + ops */}
            <div className="mb-3 text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Telephony & ops</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              <Stat label="Twilio active" value={stats.twilio_numbers_assigned} />
              <Stat label="Twilio released" value={stats.twilio_numbers_released} />
              <Stat label="Calls today" value={stats.calls_today} />
              <Stat label="Calls 7d" value={stats.calls_last_7d} />
              <Stat label="Call logs total" value={stats.call_logs_total} />
              <Stat label="Contacts" value={stats.contacts_total} />
              <Stat label="Expiring 7d" value={stats.signups_expiring_within_7d} />
              <Stat label="Total signups" value={stats.signups_total} />
            </div>
          </>
        )}

        <div className="card p-5 mb-6">
          <div className="font-display text-lg mb-3">SIGNUPS ({signups.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-signups-table">
              <thead>
                <tr className="text-left uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Plan</th>
                  <th className="py-2 pr-3">Pay</th>
                  <th className="py-2 pr-3">Sub</th>
                  <th className="py-2 pr-3">Twilio</th>
                  <th className="py-2 pr-3" title="Welcome email status">✉</th>
                  <th className="py-2 pr-3">Renews</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {signups.map((s) => (
                  <tr key={s.id} className="border-t" style={{ borderColor: "var(--line)" }}>
                    <td className="py-2 pr-3 text-white">{s.full_name}</td>
                    <td className="py-2 pr-3">{s.email}</td>
                    <td className="py-2 pr-3">{s.plan}</td>
                    <td className="py-2 pr-3"><StatusPill v={s.payment_status} /></td>
                    <td className="py-2 pr-3"><StatusPill v={s.subscription_status} /></td>
                    <td className="py-2 pr-3 font-mono text-[11px]" style={{ color: s.twilio_number ? "var(--accent)" : "var(--muted)" }}>
                      {s.twilio_number || "—"}
                    </td>
                    <td className="py-2 pr-3 text-[11px]" title={s.email_error || (s.email_sent ? "Welcome email sent" : "Welcome email not yet sent")}>
                      {s.twilio_number
                        ? (s.email_sent
                            ? <span style={{ color: "#22c55e" }}>✓</span>
                            : <span style={{ color: "var(--danger)" }} title={s.email_error || "Not sent"}>✗</span>)
                        : <span style={{ color: "var(--muted)" }}>—</span>}
                    </td>
                    <td className="py-2 pr-3 text-[11px]">{formatDate(s.current_period_end || s.expires_at)}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {s.payment_status === "paid" && !s.twilio_number && (
                        <button
                          onClick={() => provision(s.id)}
                          disabled={busyId === s.id}
                          className="btn-secondary text-[11px] mr-1"
                          data-testid={`admin-provision-${s.id}`}
                        >
                          Provision
                        </button>
                      )}
                      {s.twilio_number && (
                        <button
                          onClick={() => resendEmail(s.id)}
                          disabled={busyId === s.id}
                          className="btn-secondary text-[11px] mr-1"
                          title="Re-send the welcome email"
                          data-testid={`admin-resend-${s.id}`}
                        >
                          ✉ Resend
                        </button>
                      )}
                      {s.subscription_status === "active" && (
                        <button
                          onClick={() => cancel(s.id)}
                          disabled={busyId === s.id}
                          className="btn-secondary text-[11px] mr-1"
                          style={{ color: "var(--danger)" }}
                          data-testid={`admin-cancel-${s.id}`}
                        >
                          <Trash2 size={11} /> Cancel
                        </button>
                      )}
                      {s.payment_status === "paid" && s.stripe_customer_id && (
                        <button
                          onClick={() => refund(s.id)}
                          disabled={busyId === s.id}
                          className="btn-secondary text-[11px]"
                          style={{ color: "var(--accent)" }}
                          data-testid={`admin-refund-${s.id}`}
                          title="Refund the most recent charge"
                        >
                          $ Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {signups.length === 0 && (
                  <tr><td colSpan="9" className="py-6 text-center" style={{ color: "var(--muted)" }}>No signups yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card p-5">
          <div className="font-display text-lg mb-3">TWILIO NUMBERS ({numbers.length})</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-numbers-table">
              <thead>
                <tr className="text-left uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  <th className="py-2 pr-3">Number</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Prison</th>
                  <th className="py-2 pr-3">Forward to</th>
                  <th className="py-2 pr-3">Purchased</th>
                </tr>
              </thead>
              <tbody>
                {numbers.map((n) => (
                  <tr key={n.id} className="border-t" style={{ borderColor: "var(--line)" }}>
                    <td className="py-2 pr-3 font-mono text-[11px] text-white">{n.phone_number}</td>
                    <td className="py-2 pr-3"><StatusPill v={n.status} /></td>
                    <td className="py-2 pr-3">{n.prison || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{n.forward_to_mobile || "—"}</td>
                    <td className="py-2 pr-3 text-[11px]">{formatDate(n.created_at)}</td>
                  </tr>
                ))}
                {numbers.length === 0 && (
                  <tr><td colSpan="5" className="py-6 text-center" style={{ color: "var(--muted)" }}>No Twilio numbers yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card p-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-lg">LIVE CALL LOG (latest 20)</div>
            <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>
              Auto-refresh 30s
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-calls-table">
              <thead>
                <tr className="text-left uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">From (caller)</th>
                  <th className="py-2 pr-3">Cellblock B number</th>
                  <th className="py-2 pr-3">Forwarded to</th>
                </tr>
              </thead>
              <tbody>
                {callLogs.map((c) => (
                  <tr key={c.id} className="border-t" style={{ borderColor: "var(--line)" }}>
                    <td className="py-2 pr-3 text-[11px]">{formatDateTime(c.created_at)}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{c.from || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]" style={{ color: "var(--accent)" }}>{c.to || "—"}</td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{c.forwarded_to || "—"}</td>
                  </tr>
                ))}
                {callLogs.length === 0 && (
                  <tr><td colSpan="4" className="py-6 text-center" style={{ color: "var(--muted)" }}>No calls yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div
      className="card p-4"
      style={accent ? { borderColor: "var(--accent)" } : {}}
    >
      <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>{label}</div>
      <div className="font-display text-2xl mt-1" style={accent ? { color: "var(--accent)" } : {}}>{value ?? 0}</div>
    </div>
  );
}

function StatusPill({ v }) {
  const map = {
    paid: { bg: "rgba(34,197,94,0.12)", fg: "#22c55e" },
    active: { bg: "rgba(34,197,94,0.12)", fg: "#22c55e" },
    pending: { bg: "rgba(245,165,36,0.15)", fg: "var(--accent)" },
    past_due: { bg: "rgba(245,165,36,0.15)", fg: "var(--accent)" },
    assigned: { bg: "rgba(34,197,94,0.12)", fg: "#22c55e" },
    cancelled: { bg: "rgba(229,72,77,0.15)", fg: "var(--danger)" },
    expired: { bg: "rgba(229,72,77,0.15)", fg: "var(--danger)" },
    released: { bg: "rgba(255,255,255,0.06)", fg: "var(--muted)" },
    inactive: { bg: "rgba(255,255,255,0.06)", fg: "var(--muted)" },
    available: { bg: "rgba(255,255,255,0.06)", fg: "var(--muted)" },
  };
  const s = map[v] || map.inactive;
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: s.bg, color: s.fg }}
    >
      {v || "—"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
  } catch (e) { return "—"; }
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
      hour12: false,
    });
  } catch (e) { return "—"; }
}

function fmtAud(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function AdminPage() {
  const [token, setToken] = useState(() => {
    try { return window.sessionStorage.getItem("cbb_admin_token") || ""; } catch (e) { return ""; }
  });
  const onLogout = () => {
    try { window.sessionStorage.removeItem("cbb_admin_token"); } catch (e) { /* private-mode / storage disabled */ }
    setToken("");
  };
  if (!token) return <AdminLogin onAuthed={setToken} />;
  return <AdminDashboard token={token} onLogout={onLogout} />;
}

function App() {
  return (
    <BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
        <Route path="/manage/:signupId" element={<ManagePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/analytics-debug" element={<AnalyticsDebugPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
        <Route path="/qld-prison-calls" element={<QLDPage />} />
        <Route path="/nsw-prison-calls" element={<NSWPage />} />
        <Route path="/vic-prison-calls" element={<VICPage />} />
        <Route path="/wa-prison-calls" element={<WAPage />} />
        <Route path="/sa-prison-calls" element={<SAPage />} />
        <Route path="/tas-prison-calls" element={<TASPage />} />
        <Route path="/act-prison-calls" element={<ACTPage />} />
        <Route path="/nt-prison-calls" element={<NTPage />} />
        {PRISON_SLUGS.map((slug) => (
          <Route
            key={slug}
            path={`/${slug}-calls`}
            element={<PrisonPageFor slug={slug} />}
          />
        ))}
        <Route path="/prisonconnect-alternative" element={<PrisonConnectAlternativePage />} />
        <Route path="/cellblock-b-vs-prisonconnect" element={<CellblockBVsPrisonConnectPage />} />
        <Route path="/prisonengine-alternative" element={<PrisonEngineAlternativePage />} />
        <Route path="/cellblock-b-vs-prisonengine" element={<CellblockBVsPrisonEnginePage />} />
        <Route path="/cheapest-prison-call-service-australia" element={<CheapestPrisonCallPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
